import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { KPIGrid, KPICard } from '../../components/admin/KPICards';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Wrench, Clock, CheckCircle, XCircle, Search, User,
  AlertTriangle, Truck, Calendar, MessageSquare, Filter,
  ChevronRight, Phone, Mail, Package, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { 
    label: 'Pendiente', 
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    icon: Clock 
  },
  in_progress: { 
    label: 'En Proceso', 
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    icon: Wrench 
  },
  resolved: { 
    label: 'Resuelto', 
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    icon: CheckCircle 
  },
  closed: { 
    label: 'Cerrado', 
    color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
    icon: XCircle 
  }
};

const TYPE_CONFIG = {
  maintenance: { label: 'Mantenimiento', icon: Wrench, color: 'text-blue-400' },
  revision: { label: 'Revisión', icon: Search, color: 'text-purple-400' },
  incident: { label: 'Incidencia', icon: AlertTriangle, color: 'text-red-400' },
  removal: { label: 'Retirada', icon: Truck, color: 'text-orange-400' }
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.maintenance;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 ${config.color}`}>
      <Icon className="w-4 h-4" />
      <span className="text-sm">{config.label}</span>
    </span>
  );
}

function TicketCard({ ticket, onSelect }) {
  const typeConfig = TYPE_CONFIG[ticket.type] || TYPE_CONFIG.maintenance;
  const TypeIcon = typeConfig.icon;

  return (
    <div 
      onClick={() => onSelect(ticket)}
      className="p-5 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 hover:bg-white/[0.04] transition-all duration-300 cursor-pointer group"
      data-testid={`ticket-card-${ticket.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            ticket.status === 'pending' ? 'bg-amber-500/20' :
            ticket.status === 'in_progress' ? 'bg-blue-500/20' :
            ticket.status === 'resolved' ? 'bg-emerald-500/20' :
            'bg-zinc-500/20'
          }`}>
            <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-white font-mono">{ticket.ticket_number}</p>
            <p className="text-xs text-zinc-500">{typeConfig.label}</p>
          </div>
        </div>
        <StatusBadge status={ticket.status} />
      </div>

      <p className="text-sm text-zinc-300 line-clamp-2 mb-4">{ticket.description}</p>

      <div className="space-y-2 text-xs text-zinc-500">
        <div className="flex items-center gap-2">
          <User className="w-3.5 h-3.5" />
          <span>{ticket.client_name}</span>
        </div>
        {ticket.equipment_name && (
          <div className="flex items-center gap-2">
            <Package className="w-3.5 h-3.5" />
            <span>{ticket.equipment_name}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Calendar className="w-3.5 h-3.5" />
          <span>{new Date(ticket.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        </div>
      </div>

      {ticket.assigned_technician && (
        <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
          <Wrench className="w-3.5 h-3.5 text-blue-400" />
          <span className="text-xs text-zinc-400">Técnico: <span className="text-white">{ticket.assigned_technician}</span></span>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 group-hover:translate-x-0.5 transition-all" />
      </div>
    </div>
  );
}

function TicketDetailModal({ ticket, isOpen, onClose, onUpdate }) {
  const [status, setStatus] = useState(ticket?.status || 'pending');
  const [technician, setTechnician] = useState(ticket?.assigned_technician || '');
  const [internalNotes, setInternalNotes] = useState(ticket?.internal_notes || '');
  const [resolutionNotes, setResolutionNotes] = useState(ticket?.resolution_notes || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setTechnician(ticket.assigned_technician || '');
      setInternalNotes(ticket.internal_notes || '');
      setResolutionNotes(ticket.resolution_notes || '');
    }
  }, [ticket, setStatus, setTechnician, setInternalNotes, setResolutionNotes]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/tickets/${ticket.id}`, {
        status,
        assigned_technician: technician || null,
        internal_notes: internalNotes || null,
        resolution_notes: resolutionNotes || null
      }, { withCredentials: true });
      
      toast.success('Ticket actualizado correctamente');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error('Error al actualizar el ticket');
    } finally {
      setSaving(false);
    }
  };

  if (!ticket) return null;

  const typeConfig = TYPE_CONFIG[ticket.type] || TYPE_CONFIG.maintenance;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-['Outfit']">
            <span className="font-mono text-sky-400">{ticket.ticket_number}</span>
            <TypeBadge type={ticket.type} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Client Info */}
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Datos del Cliente</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-zinc-500" />
                <span className="text-white">{ticket.client_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-300">{ticket.client_email}</span>
              </div>
              {ticket.client_phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-300">{ticket.client_phone}</span>
                </div>
              )}
              {ticket.equipment_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4 text-zinc-500" />
                  <span className="text-zinc-300">{ticket.equipment_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Descripción</h4>
            <p className="text-sm text-zinc-300 p-3 rounded-lg bg-white/[0.02] border border-white/5">
              {ticket.description}
            </p>
          </div>

          {/* Status & Technician */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Estado</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-zinc-300">
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Técnico Asignado</label>
              <Input
                value={technician}
                onChange={(e) => setTechnician(e.target.value)}
                placeholder="Nombre del técnico"
                className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600"
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
              <MessageSquare className="w-3 h-3 inline mr-1" />
              Notas Internas (no visibles para el cliente)
            </label>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Añadir notas internas..."
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 min-h-[80px]"
            />
          </div>

          {/* Resolution Notes */}
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
              <FileText className="w-3 h-3 inline mr-1" />
              Notas de Resolución (visibles para el cliente)
            </label>
            <Textarea
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              placeholder="Describir la resolución..."
              className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 min-h-[80px]"
            />
          </div>

          {/* Dates */}
          <div className="flex items-center gap-6 text-xs text-zinc-500">
            <span>Creado: {new Date(ticket.created_at).toLocaleString('es-ES')}</span>
            {ticket.updated_at && <span>Actualizado: {new Date(ticket.updated_at).toLocaleString('es-ES')}</span>}
            {ticket.resolved_at && <span>Resuelto: {new Date(ticket.resolved_at).toLocaleString('es-ES')}</span>}
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-zinc-300 hover:bg-white/5">
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-white text-black hover:bg-zinc-200">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTickets() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/tickets`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/tickets/stats`, { withCredentials: true })
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSelectTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowModal(true);
  };

  // Filter tickets
  const filteredTickets = tickets.filter(t => {
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      t.ticket_number?.toLowerCase().includes(query) ||
      t.client_name?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query);
    return matchesStatus && matchesSearch;
  });

  const pendingCount = stats?.pending || 0;

  if (loading) {
    return (
      <AdminLayout title="Gestión Técnica" onLogout={handleLogout}>
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-white/10 border-t-white rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestión Técnica" onLogout={handleLogout}>
      {/* Stats KPIs */}
      <KPIGrid className="mb-8">
        <KPICard
          title="Pendientes"
          value={stats?.pending || 0}
          icon={Clock}
          variant={pendingCount > 0 ? 'warning' : 'default'}
        />
        <KPICard
          title="En Proceso"
          value={stats?.in_progress || 0}
          icon={Wrench}
          variant="accent"
        />
        <KPICard
          title="Resueltos"
          value={stats?.resolved || 0}
          icon={CheckCircle}
          variant="success"
        />
        <KPICard
          title="Esta Semana"
          value={stats?.this_week || 0}
          icon={Calendar}
          variant="highlight"
        />
      </KPIGrid>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-400">
              {pendingCount} ticket{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} de asignación
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => setFilterStatus('pending')}
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            Ver pendientes
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
          {/* Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Buscar por ticket, cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 bg-white/5 border-white/10 text-white text-sm placeholder:text-zinc-500"
              data-testid="tickets-search-input"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48 h-9 bg-white/5 border-white/10 text-white text-sm" data-testid="tickets-status-filter">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="all" className="text-zinc-300">Todos ({stats?.total || 0})</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key} className="text-zinc-300">
                    {config.label} ({stats?.[key] || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-sm text-zinc-500">{filteredTickets.length} resultado{filteredTickets.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Tickets Grid */}
      {filteredTickets.length === 0 ? (
        <div className="text-center py-12">
          <Wrench className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
          <p className="text-zinc-500">No hay tickets {filterStatus !== 'all' ? `con estado "${STATUS_CONFIG[filterStatus]?.label}"` : ''}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTickets.map(ticket => (
            <TicketCard key={ticket.id} ticket={ticket} onSelect={handleSelectTicket} />
          ))}
        </div>
      )}

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onUpdate={fetchData}
      />
    </AdminLayout>
  );
}
